import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';
import { ACCOUNT_ROUTE_PATH } from '../enums/routes/account-route-path.enum';

/**
 * @description A custom RouteReuseStrategy that caches routes based on a simple rule:
 * If the route's component is listed as reusable, it will be stored and reused.
 */
export class ProductsRouteReuseStrategy implements RouteReuseStrategy {
  // A map to store detached route handles keyed by route paths.
  private storedRoutes = new Map<string, DetachedRouteHandle>();

  /**
   * @description Determines if this route (and its component) should be detached for reuse later.
   * Return true if you want to cache this route.
   */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const path = route.routeConfig?.path;

    return !!path && path === ACCOUNT_ROUTE_PATH.products;
  }

  /**
   * @description Stores the detached route handle so it can be reattached when returning to this route.
   */
  store(route: ActivatedRouteSnapshot, detachedTree: DetachedRouteHandle | null): void {
    const path = route.routeConfig?.path;

    if (!path) {
      return
    }

    if (detachedTree === null) {
      this.storedRoutes.delete(path);

      return;
    }

    this.storedRoutes.set(path, detachedTree);
  }

  /**
   * @description Determines if this route should be reattached.
   * Return true if a stored route handle should be used instead of creating a new component.
   */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const path = route.routeConfig?.path;

    return !!path && this.storedRoutes.has(path);
  }

  /**
   * @description Retrieves the previously stored route handle to reattach.
   */
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const path = this.extractPath(route);

    if (!path) {
      return null;
    }

    const stored = this.findStoredRoute(path);

    if (!stored) {
      return null;
    }

    return this.validateAndCleanup(stored, path);
  }

  private extractPath(route: ActivatedRouteSnapshot): string | undefined {
    return route.routeConfig?.path;
  }

  private findStoredRoute(path: string): unknown {
    return this.storedRoutes.get(path);
  }

  private validateAndCleanup(stored: unknown, path: string): DetachedRouteHandle | null {
    const isValid = stored &&
      typeof stored === 'object' &&
      'componentRef' in stored &&
      'route' in stored &&
      'contexts' in stored;

    if (isValid) {
      return stored as DetachedRouteHandle;
    }

    this.storedRoutes.delete(path);

    return null;
  }

  /**
   * @description Determines if the route should be reused. If returns false, the route and its component
   * will be recreated.
   * Normally, this returns true if the future and current routes have the same configuration.
   */
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }
}
